import type { FC, ReactNode } from 'react'

import { useRef } from 'react'

import { gsap, useGsap, prefersReducedMotion } from '@/lib/gsap'
import { LiquidImage } from '@/components/generals/liquid-image'
import { SplitText } from '@/components/generals/split-text'
import { JustifiedSplit } from '@/components/studio/justified-split'
import { media } from '@/constants/media'
import { studioMeta } from '@/constants/site'
import { socials } from '@/constants/socials'

const INTRO = "A brand and web studio for founders who care how they're seen. Small on purpose, and personal by design."

const STATS = [
    { value: studioMeta.established, label: 'Established' },
    { value: '50+', label: 'Brands launched' },
    { value: '2', label: 'Clients a month' },
    { value: '24hr', label: 'Reply, at the latest' }
]

/* A split hero rather than a headline floating on a full-bleed photograph.
   The left is a dark editorial column that carries the title, the intro, the
   numbers and the studio's details; the right is a tall plate with a second
   frame overlapping its corner and a vertical service rail down its edge. The
   old version was one picture with two lines of type on it, which is why two
   thirds of the screen were empty. */
export const StudioHero: FC = (): ReactNode => {
    const scope = useRef<HTMLElement>(null)

    useGsap(() => {
        const root = scope.current

        if (!root) {
            return
        }

        const items = root.querySelectorAll<HTMLElement>('[data-hero-foot-item]')
        const plate = root.querySelector<HTMLElement>('[data-hero-plate]')
        const inset = root.querySelector<HTMLElement>('[data-hero-inset]')

        if (prefersReducedMotion()) {
            gsap.set(items, { opacity: 1, y: 0 })
            gsap.set([plate, inset].filter(Boolean), { opacity: 1, y: 0, clipPath: 'inset(0%)' })

            return
        }

        /* The column arrives one block at a time behind the headline. */
        gsap.fromTo(items,
            { opacity: 0, y: 38 },
            {
                opacity: 1,
                y: 0,
                duration: 1.1,
                delay: 0.8,
                stagger: 0.09,
                ease: 'noma'
            }
        )

        if (plate) {
            gsap.fromTo(plate,
                { clipPath: 'inset(100% 0% 0% 0%)' },
                {
                    clipPath: 'inset(0% 0% 0% 0%)',
                    duration: 1.6,
                    delay: 0.45,
                    ease: 'noma'
                }
            )

            /* The plate lags the page on the first scroll, which is where the
               hero's depth comes from. */
            gsap.fromTo(plate.querySelector('[data-hero-backdrop]'),
                { yPercent: -6 },
                {
                    yPercent: 8,
                    ease: 'none',
                    scrollTrigger: {
                        trigger: root,
                        start: 'top top',
                        end: 'bottom top',
                        scrub: 0.6
                    }
                }
            )
        }

        if (inset) {
            gsap.fromTo(inset,
                { opacity: 0, y: 54, rotate: -3 },
                {
                    opacity: 1,
                    y: 0,
                    rotate: 0,
                    duration: 1.3,
                    delay: 1.05,
                    ease: 'noma'
                }
            )

            gsap.to(inset, {
                yPercent: -14,
                ease: 'none',
                scrollTrigger: {
                    trigger: root,
                    start: 'top top',
                    end: 'bottom top',
                    scrub: 0.8
                }
            })
        }
    }, scope, [])

    return (
        <section
            id='studio-hero'
            ref={scope}
            aria-labelledby='studio-hero-title'
            className='relative bg-ink p-[3px] text-paper'
        >
            <div className='grid gap-[3px] lg:min-h-[100svh] lg:grid-cols-12'>
                {/* Left: the editorial column */}
                <div className='flex flex-col justify-between gap-10 rounded-[var(--radius-frame)] bg-ink-soft p-5 pt-[calc(var(--nav-h)+28px)] lg:col-span-7 lg:gap-14 lg:p-[42px] lg:pt-[calc(var(--nav-h)+56px)]'>
                    <div className='flex flex-col gap-7'>
                        <span data-hero-foot-item className='t-mono flex items-center gap-3 text-ash-soft'>
                            <span className='block h-px w-8 bg-brand' />

                            <span>
                                (Studio)
                            </span>

                            <span className='text-paper'>
                                {studioMeta.bases.join(' / ')}
                            </span>
                        </span>

                        <SplitText
                            as='h1'
                            id='studio-hero-title'
                            text='the studio.'
                            trigger='mount'
                            delay={0.35}
                            stagger={0.035}
                            duration={1.2}
                            className='t-display text-paper'
                            style={{ fontSize: 'clamp(58px, 9vw, 128px)' }}
                        />

                        <JustifiedSplit
                            as='p'
                            runs={[{ text: INTRO }]}
                            indent={27}
                            trigger='mount'
                            delay={0.75}
                            stagger={0.007}
                            className='t-cond max-w-[46ch] text-paper'
                            style={{
                                fontSize: 'clamp(17px, 1.5vw, 21px)',
                                lineHeight: 1.14
                            }}
                        />
                    </div>

                    <div
                        data-hero-foot-item
                        className='grid grid-cols-2 gap-x-5 gap-y-6 sm:grid-cols-4'
                    >
                        {STATS.map((stat) => (
                            <div key={stat.label} className='hair-t flex flex-col gap-1.5 pt-3.5'>
                                <span className='t-display nums text-[clamp(21px,2vw,30px)] leading-none text-paper'>
                                    {stat.value}
                                </span>

                                <span className='t-mono text-ash-soft'>
                                    {stat.label}
                                </span>
                            </div>
                        ))}
                    </div>

                    <div className='hair-t grid grid-cols-2 gap-x-5 gap-y-8 pt-7 lg:grid-cols-3'>
                        <div data-hero-foot-item className='flex flex-col gap-2.5'>
                            <span className='t-mono text-ash-soft'>
                                based in
                            </span>

                            {studioMeta.bases.map((base) => (
                                <span key={base} className='t-mono text-paper'>
                                    {base}
                                </span>
                            ))}
                        </div>

                        <div data-hero-foot-item className='flex flex-col gap-2.5'>
                            <span className='t-mono text-ash-soft'>
                                Services
                            </span>

                            {studioMeta.services.map((service) => (
                                <span key={service} className='t-mono text-paper'>
                                    {service}
                                </span>
                            ))}
                        </div>

                        <ul
                            data-hero-foot-item
                            className='col-span-2 flex items-end justify-start gap-5 lg:col-span-1 lg:justify-end'
                        >
                            {socials.map((social) => (
                                <li key={social.label}>
                                    <a
                                        href={social.href}
                                        target='_blank'
                                        rel='noreferrer'
                                        data-cursor='link'
                                        aria-label={social.label}
                                        className='block text-paper transition-colors duration-500 hover:text-brand'
                                    >
                                        <svg
                                            viewBox='0 0 24 24'
                                            className='size-[18px]'
                                            aria-hidden='true'
                                            focusable='false'
                                        >
                                            <path d={social.path} fill='currentColor' />
                                        </svg>
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>

                {/* Right: the plate, with a second frame breaking its corner */}
                <div className='relative min-h-[62svh] lg:col-span-5 lg:min-h-0'>
                    <div
                        data-hero-plate
                        className='absolute inset-0 overflow-hidden rounded-[var(--radius-frame)] bg-ink-soft'
                    >
                        <div data-hero-backdrop className='absolute inset-x-0 -top-[8%] h-[116%]'>
                            <LiquidImage
                                sources={[media.studioCulture]}
                                alt='The Noma Studio team at work in the Los Angeles studio'
                                priority
                                intensity={0.6}
                                className='size-full'
                            />
                        </div>

                        <span
                            aria-hidden='true'
                            className='absolute inset-0 bg-[linear-gradient(to_top,rgba(5,5,5,0.62),rgba(5,5,5,0.05))]'
                        />

                        {/* The service rail, set on its side down the right edge. */}
                        <span
                            aria-hidden='true'
                            className='t-mono absolute right-4 top-1/2 hidden -translate-y-1/2 text-paper/60 lg:block'
                            style={{ writingMode: 'vertical-rl' }}
                        >
                            Brand identity / Art direction / Web design
                        </span>
                    </div>

                    <div
                        data-hero-inset
                        className='absolute -bottom-2 left-4 z-[2] w-[38%] max-w-[210px] overflow-hidden rounded-[var(--radius-card)] border border-paper/15 bg-ink-soft shadow-[0_24px_60px_-24px_rgba(0,0,0,0.8)] lg:-left-10 lg:bottom-10 lg:w-[48%]'
                    >
                        <img
                            src={media.editorial.deskLight}
                            alt='Late light across the studio desk'
                            loading='eager'
                            decoding='async'
                            className='aspect-[4/5] w-full object-cover'
                        />
                    </div>
                </div>
            </div>
        </section>
    )
}
